// https://fundboard.pacificlife.com/enroll-ui/fundboard
import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { QuestionCircle } from "react-bootstrap-icons";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import {
    FUNDBOARD,
    REQUIRED_FIELDS,
    FUNDBOARD_LOGIN_PAGE_URL_PATH
} from "./constants";
import {
    emailValidation,
    userNameValidation,
    emailVerifyMatch
} from "./validation";
import { analytics } from "./analytics";
import { Header, Footer, Disclaimer } from "./components";

const FundboardReg = () => {
    const [user, setUser] = useState({
        role: FUNDBOARD,
        firstName: "",
        lastName: "",
        email: "",
        verifyEmail: "",
        firmId: "",
        crd: "",
        contractNumber: "",
        tin: "",
        dob: "",
        id: "",
        bdName: "bd name here",
        activeContract: null,
        plRelation: null
    });
    const [errors, setErrors] = useState({
        email: "",
        verifyEmail: "",
        id: ""
    });
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        // check if all fields are filled out
        const requiredFields = REQUIRED_FIELDS[user.role];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
        }
        // check if there is any error
        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }
        setSubmitDisabled(false);
    }, [user, errors]);

    useEffect(() => {
        document.title = "Fundboard Registration | Pacific Life";
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const payload = { [name]: value };
        setUser((prev) => ({ ...prev, ...payload }));
    };

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        // check if all fields are filled out
        const role = user.role;
        const requiredFields = REQUIRED_FIELDS[role];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
        }
        // check if there is any error
        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }
        setSubmitDisabled(false);
    }, [user, errors]);

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";
        if (name === "email") {
            const emailValid = emailValidation(value);
            errorMessage = emailValid ? "" : "Enter valid email";
        }

        if (name === "verifyEmail") {
            errorMessage = emailVerifyMatch(user.email, value)
                ? ""
                : "Email addresses do not match";
        }

        if (name === "id") {
            const valid = userNameValidation(value);
            const tooShortOrLong = value.length < 6 || value.length > 8;
            const hasSpecialCharOrWhiteSpace = /\W|\D/g.test(value);
            if (valid) {
                errorMessage = "";
            } else if (tooShortOrLong) {
                errorMessage =
                    "Username must be between 6 and 8 characters long";
            } else if (hasSpecialCharOrWhiteSpace) {
                errorMessage =
                    "Username must not contain spaces or special characters";
            }
        }

        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const handleCancel = () => {
        document.location.href = FUNDBOARD_LOGIN_PAGE_URL_PATH;
    };

    const formatPayload = () => {
        const payload = {};
        const role = user.role;
        const requiredFields = REQUIRED_FIELDS[role];
        for (const element of requiredFields) {
            const fieldName = element;
            payload[fieldName] = user[fieldName];
        }
        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = formatPayload();
        setSubmitting(true);
        try {
            await axios({
                method: "POST",
                url: "/enroll/fundboard/register",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            });
            document.location.href =
                "/authenticate/successful_registration.html";
        } catch (error) {
            setToastMessage(
                error.response?.data?.errorMessage ||
                    "Something went wrong. Please try again later."
            );
        }
        setSubmitting(false);
    };

    const closeToast = () => {
        setToastMessage("");
    };

    return (
        <>
            {toastMessage !== "" && (
                <Toast onClose={closeToast} className="toast-cont">
                    <Toast.Header>
                        <strong className="me-auto">{toastMessage}</strong>
                    </Toast.Header>
                </Toast>
            )}
            <Header />
            <div className="mainMyAccount">
                <h1 className="heading" style={{ marginBottom: "2%" }}>
                    Fundboard Website Registration
                </h1>
                <div className="subHeading" style={{ marginBottom: "3%" }}>
                    Create an online account to access the Pacific Select Fund &
                    Pacific Funds Board materials
                </div>
                <Row>
                    <Col sm={6}>
                        <label className="sub-title">
                            Personal Information
                        </label>
                    </Col>
                </Row>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                name="firstName"
                                value={user.firstName}
                                maxLength={25}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                name="lastName"
                                value={user.lastName}
                                maxLength={35}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip multiline />
                            <Form.Label className="emailClass">
                                Email Address
                                <QuestionCircle
                                    className="iconClass"
                                    data-tip="Your password link will be sent to the email address you supply here"
                                    tabIndex="-1"
                                />
                            </Form.Label>
                            <Form.Control
                                name="email"
                                value={user.email}
                                maxLength={60}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.email}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Verify Email Address</Form.Label>
                            <Form.Control
                                name="verifyEmail"
                                value={user.verifyEmail}
                                maxLength={60}
                                onChange={handleChange}
                                onPaste={(e) => e.preventDefault()}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.verifyEmail}
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <label className="sub-title">
                                Login Information
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip multiline />
                            <Form.Label className="createUsername">
                                User Name
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Remember this! You will need it along with your password to access your account information"
                                />
                            </Form.Label>
                            <Form.Control
                                name="id"
                                autoComplete="off"
                                maxLength={8}
                                value={user.id}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.id}
                            </span>
                        </Col>
                        <Col md={6}>
                            <div className="sublabel">
                                Must be between 6 and 8 characters
                                <br />
                                May contain letters and numbers
                                <br />
                                No spaces or special characters
                            </div>
                        </Col>
                    </Row>
                    <div className="buttons-myaccount">
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                }
                            }}
                            data-track="analytics"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={handleSubmit}
                            data-track="analytics"
                            disabled={submitDisabled || submitting}
                        >
                            {submitting ? "Registering" : "Register"}
                        </Button>
                    </div>
                </Form>
                <div id="login" className="link-underline">
                    <a href={FUNDBOARD_LOGIN_PAGE_URL_PATH}>
                        Already have an account?
                    </a>
                </div>
                <Disclaimer />
            </div>
            <Footer />
        </>
    );
};

export default FundboardReg;
